import React, { useState } from "react";
import { ArrowRight, Download, Star, BookOpen, Sparkles } from "lucide-react";
import { products, shopTabs } from "../mock";
import { useToast } from "../hooks/use-toast";

const Shop = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState("personal");

  const handleBuy = (p) => {
    if (p.comingSoon) {
      toast({
        title: "You're on the list",
        description: `We'll email you the moment "${p.title}" is available.`,
      });
      return;
    }
    toast({
      title: p.price === "Free" ? "On its way" : "Reserved with love",
      description:
        p.price === "Free"
          ? `${p.title} is being sent to your inbox.`
          : `${p.title} — ${p.price}. Full checkout activates when the shop backend is live.`,
    });
  };

  const filtered = products.filter((p) => p.category === tab);

  return (
    <section id="shop" className="relative py-28 md:py-36 overflow-hidden">
      <div className="cosmic-bg absolute inset-0" />
      <div className="stars" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="divider-star justify-center"><span>The Sacred Shop</span></div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-8 text-[#EDE3D0]">
            Take the frequency
            <span className="italic text-[#D4A85B]"> home with you.</span>
          </h2>
          <p className="mt-6 text-[#EDE3D0]/75 leading-relaxed">
            Therapist-crafted, spirit-led resources for personal healing — and trauma-informed
            field guides for the educators shaping the next generation.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-12 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-[#D4A85B]/30 bg-[#12081E]/60 p-1.5 backdrop-blur">
            {shopTabs.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-5 md:px-6 py-2.5 text-[11px] uppercase tracking-[0.28em] transition ${
                    active
                      ? "bg-[#D4A85B] text-[#0A0714]"
                      : "text-[#EDE3D0]/75 hover:text-[#D4A85B]"
                  }`}
                >
                  {t.id === "personal" ? <Sparkles size={13} /> : <BookOpen size={13} />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className={`mt-14 grid gap-6 ${filtered.length === 2 ? "md:grid-cols-2 max-w-4xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {filtered.map((p) => (
            <article
              key={p.id}
              className={`group relative rounded-3xl overflow-hidden border transition duration-500 ${p.landscape ? "flex flex-col md:flex-row md:col-span-2" : "flex flex-col"} ${
                p.featured
                  ? "border-[#D4A85B] bg-[#0F0821] shadow-[0_30px_80px_-30px_rgba(212,168,91,0.35)]"
                  : "border-[#D4A85B]/25 bg-[#12081E]/70 hover:border-[#D4A85B]/60"
              } ${p.comingSoon ? "opacity-90" : ""}`}
            >
              {p.featured && !p.comingSoon && (
                <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 rounded-full bg-[#D4A85B] text-[#0A0714] text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">
                  <Star size={10} fill="#0A0714" /> Featured
                </div>
              )}

              <div className={`relative overflow-hidden bg-gradient-to-b from-[#1A0E30] to-[#0A0714] ${p.landscape ? "md:w-1/2 aspect-[2/1] md:aspect-auto md:min-h-[280px]" : "aspect-[3/4]"}`}>
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    className={`w-full h-full ${p.landscape ? "object-cover" : (p.id === "vibrate-higher" || p.id === "when-students-hurt" || p.id === "when-helping-hurts") ? "object-contain" : "object-cover"} group-hover:scale-[1.03] transition duration-700`}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
                    <div className="w-20 h-20 rounded-full border border-[#D4A85B]/40 flex items-center justify-center text-[#D4A85B] mb-6">
                      <BookOpen size={28} strokeWidth={1.4} />
                    </div>
                    <div className="font-serif text-3xl text-[#EDE3D0] italic">Coming Soon</div>
                    <div className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/85">
                      Cover reveal ahead
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#12081E] via-transparent to-transparent pointer-events-none" />
                <span className="absolute top-4 left-4 bg-[#0A0714]/80 backdrop-blur border border-[#D4A85B]/30 text-[#D4A85B] text-[10px] uppercase tracking-[0.28em] rounded-full px-3 py-1.5">
                  {p.tag}
                </span>
              </div>

              <div className={`p-7 flex-1 flex flex-col ${p.landscape ? "md:w-1/2" : ""}`}>
                <h3 className="font-serif text-2xl text-[#EDE3D0] leading-tight">{p.title}</h3>
                {p.subtitle && (
                  <div className="italic font-serif text-[#D4A85B]/90 mt-2 text-sm leading-snug">{p.subtitle}</div>
                )}
                <p className="mt-4 text-sm text-[#EDE3D0]/70 leading-relaxed flex-1">{p.description}</p>

                <div className="mt-6 pt-5 border-t border-[#D4A85B]/15 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-2xl text-[#D4A85B]">{p.price}</div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#EDE3D0]/55 flex items-center gap-1.5 mt-1">
                      <Download size={11} /> {p.priceNote}
                    </div>
                  </div>
                  {p.href ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A85B] text-[#D4A85B] hover:bg-[#D4A85B] hover:text-[#0A0714] transition px-4 py-2.5 text-[11px] uppercase tracking-[0.24em]"
                    >
                      {p.button} <ArrowRight size={12} />
                    </a>
                  ) : (
                    <button
                      onClick={() => handleBuy(p)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A85B] text-[#D4A85B] hover:bg-[#D4A85B] hover:text-[#0A0714] transition px-4 py-2.5 text-[11px] uppercase tracking-[0.24em]"
                    >
                      {p.button} <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        {tab === "educator" && (
          <p className="mt-10 text-center text-sm text-[#EDE3D0]/60 max-w-2xl mx-auto">
            The Trauma-Informed Schools Series — resources written from a counseling chair,
            for the adults holding the next generation.
          </p>
        )}
      </div>
    </section>
  );
};

export default Shop;
